import { mapTargetCTA, mapTargetLink, mapTargetNonRepetitiveLink } from '@/App.datalayer';
import {
  LIST_CARD_CAROUSEL_EVENTS,
  LIST_CARD_CAROUSEL_EVENTS_NAMES,
  onDataLayerCardCarousel,
} from '@/features/shared/portal/PortalCarousel/PortalCarousel.datalayer';
import { MaxWidth, Theme } from '@/utils/constants';
import clsx from 'clsx';
import React, { memo, useMemo } from 'react';
import { withGTMPipe } from '../shared/HOCs/withGTM';
import { ComplexTabListProps } from './ComplexTabList.interface';
import useStyles from './ComplexTabList.style';
import HorizontalLogosContent from './HorizontalLogosContent';
import {
  mapTarget as mapTargetHorizontalLogosContent,
  onDatalayer as onDataLayerHorizontalLogosContent,
  onDatalayerTabClick as onDataLayerHorizontalLogosContentTabClick,
} from './HorizontalLogosContent/HorizontalLogosContent.datalayer';
import HorizontalTopContent from './HorizontalTopContent';
import {
  mapTarget as mapTargetHorizontalTopContent,
  onDatalayer as onDataLayerHorizontalTopContent,
  onDatalayerTabClick as onDataLayerHorizontalTopContentTabClick,
} from './HorizontalTopContent/HorizontalTopContent.datalayer';
import PersonaTabs from './PersonaTabs';
import {
  mapTarget as mapTargetPersonaTabs,
  onDatalayer as onDataLayerPersonaTabs,
  onDatalayerTabClick as onDataLayerPersonaTabsTabClick,
} from './PersonaTabs/PersonaTabs.datalayer';
import TabbedCard from './TabbedCard/TabbedCard';
import { mapTabbedCardTarget, onDataLayerTabbedCardClick } from './TabbedCard/TabbedCard.datalayer';
import VerticalContent from './VerticalContent';
import {
  mapTarget as mapTargetVerticalContent,
  onDatalayer as onDataLayerVerticalContent,
  onDatalayerTabClick as onDataLayerVerticalContentTabClick,
} from './VerticalContent/VerticalContent.datalayer';
import VerticalContentFeatured from './VerticalContentFeatured';
import {
  mapTarget as mapTargetVerticalContentFeatured,
  onDatalayer as onDataLayerVerticalContentFeatured,
  onDatalayerScroll as onDataLayerScrollVerticalContentFeatured,
  onDataLayerScrollCarouselTab,
  SCROLL_CARD_CAROUSEL_EVENTS,
  SCROLL_CAROUSEL_EVENTS_NAMES,
} from './VerticalContentFeatured/VerticalContentFeatured.datalayer';
import VerticalThumbnailContent from './VerticalThumbnailContent';
import {
  mapTarget as mapTargetVerticalThumbnailContent,
  onDatalayer as onDataLayerVerticalThumbnailContent,
  onDatalayerTabClick as onDataLayerVerticalThumbnailContentTabClick,
} from './VerticalThumbnailContent/VerticalThumbnailContent.datalayer';
import VerticalWithCarousel from './VerticalWithCarousel';
import {
  mapTarget as mapTargetVerticalWithCarousel,
  onDatalayer as onDataLayerVerticalWithCarousel,
  onDatalayerTabClick as onDataLayerVerticalWithCarouselTabClick,
} from './VerticalWithCarousel/VerticalWithCarousel.datalayer';

const mapTemplateToComponent = (template = '') => {
  if (/thumbnail/i.test(template)) {
    return withGTMPipe(
      {
        onDatalayer: onDataLayerVerticalThumbnailContent,
        config: { mapTarget: mapTargetVerticalThumbnailContent },
      },
      {
        onDatalayer: onDataLayerVerticalThumbnailContentTabClick,
        config: { mapTarget: mapTargetVerticalThumbnailContent },
      },
    )(VerticalThumbnailContent);
  }

  if (/feature/i.test(template)) {
    return withGTMPipe(
      {
        onDatalayer: onDataLayerVerticalContentFeatured,
        config: { mapTarget: mapTargetVerticalContentFeatured },
      },
      {
        onDatalayer: onDataLayerScrollVerticalContentFeatured,
        config: {
          eventName: 'gtm:scroll',
          isCustom: true,
        },
      },
      {
        onDatalayer: onDataLayerScrollCarouselTab({}),
        config: { mapTarget: mapTargetNonRepetitiveLink },
      },
      {
        onDatalayer: onDataLayerScrollCarouselTab({
          event: SCROLL_CARD_CAROUSEL_EVENTS.ctaClick,
          eventName: SCROLL_CAROUSEL_EVENTS_NAMES.ctaClick,
        }),
        config: { mapTarget: mapTargetCTA },
      },
    )(VerticalContentFeatured);
  }

  if (/bottom/i.test(template)) {
    return withGTMPipe(
      {
        onDatalayer: onDataLayerHorizontalLogosContent,
        config: { mapTarget: mapTargetHorizontalLogosContent },
      },
      {
        onDatalayer: onDataLayerHorizontalLogosContentTabClick,
        config: { mapTarget: mapTargetHorizontalLogosContent },
      },
    )(HorizontalLogosContent);
  }

  if (/top/i.test(template)) {
    return withGTMPipe(
      {
        onDatalayer: onDataLayerHorizontalTopContent,
        config: { mapTarget: mapTargetHorizontalTopContent },
      },
      {
        onDatalayer: onDataLayerHorizontalTopContentTabClick,
        config: { mapTarget: mapTargetHorizontalTopContent },
      },
    )(HorizontalTopContent);
  }

  if (/persona/i.test(template)) {
    return withGTMPipe(
      {
        onDatalayer: onDataLayerPersonaTabs,
        config: { mapTarget: mapTargetPersonaTabs },
      },
      {
        onDatalayer: onDataLayerPersonaTabsTabClick,
        config: { mapTarget: mapTargetPersonaTabs },
      },
    )(PersonaTabs);
  }

  if (/vertical-tab-card/i.test(template)) {
    return withGTMPipe(
      {
        onDatalayer: onDataLayerVerticalWithCarousel,
        config: { mapTarget: mapTargetVerticalWithCarousel },
      },
      {
        onDatalayer: onDataLayerScrollVerticalContentFeatured,
        config: {
          eventName: 'gtm:custom:scroll',
          isCustom: true,
        },
      },
      {
        onDatalayer: onDataLayerCardCarousel({
          event: LIST_CARD_CAROUSEL_EVENTS.cardClick,
          eventName: LIST_CARD_CAROUSEL_EVENTS_NAMES.cardClick,
        }),
        config: { mapTarget: mapTargetLink },
      },
      {
        onDatalayer: onDataLayerVerticalWithCarouselTabClick,
        config: { mapTarget: mapTargetVerticalWithCarousel },
      },
    )(VerticalWithCarousel);
  }

  if (/tabbed-card/i.test(template)) {
    return withGTMPipe({
      onDatalayer: onDataLayerTabbedCardClick,
      config: { mapTarget: mapTabbedCardTarget },
    })(TabbedCard);
  }

  return withGTMPipe(
    {
      onDatalayer: onDataLayerVerticalContent,
      config: { mapTarget: mapTargetVerticalContent },
    },
    {
      onDatalayer: onDataLayerVerticalContentTabClick,
      config: { mapTarget: mapTargetVerticalContent },
    },
  )(VerticalContent);
};

const mapTemplateToContainerClass = ({ classes, template = '' }: any) => {
  if (/top|vertical-tab-card/i.test(template)) {
    return clsx(classes.largeContainer, classes.snap);
  }

  return 'large';
};

const ComplexTabList = ({
  template = '',
  containerBackgroundImage,
  containerBottomBackgroundColor,
  containerTopBackgroundColor,
  containerBottomSpacing,
  containerTopSpacing,
  content,
  tabColor,
  theme = Theme.light,
  contentButtons,
  backgroundOverlayOpacity,
  hasTextShadow,
}: Br.PropsWithContentButtons<ComplexTabListProps>) => {
  const classes = useStyles({});
  const hasBackgroundImage = useMemo(
    () => /top/.test(template) || /vertical-tab-card/.test(template),
    [template],
  );

  const containerProps = useMemo(
    () => ({
      bottomBackgroundColor: containerBottomBackgroundColor,
      mobileBottomBackgroundColor: tabColor,
      topBackgroundColor: containerTopBackgroundColor,
      mobileTopBackgroundColor: tabColor,
      bottomSpacing: containerBottomSpacing,
      topSpacing: containerTopSpacing,
      backgroundImage: hasBackgroundImage ? containerBackgroundImage : undefined,
      overlay: hasBackgroundImage
        ? {
            hasTextShadow,
            opacity: backgroundOverlayOpacity,
            alignment: 'left',
            maxWidth: MaxWidth.large,
          }
        : { alignment: 'left' },
      className: mapTemplateToContainerClass({ classes, template }),
    }),
    [
      backgroundOverlayOpacity,
      classes,
      containerBackgroundImage,
      containerBottomBackgroundColor,
      containerBottomSpacing,
      containerTopBackgroundColor,
      containerTopSpacing,
      hasBackgroundImage,
      hasTextShadow,
      tabColor,
      template,
    ],
  );

  const Variant = useMemo(() => mapTemplateToComponent(template), [template]);

  return (
    <section data-testid="qa-complex-tab-list-component" className={classes.root}>
      <Variant
        containerProps={containerProps}
        tabGroupTitle={content?.tabGroupTitle}
        tabGroupDescription={content?.tabGroupDescription}
        tabs={content?.tabContent}
        theme={theme}
        tabColor={tabColor}
        contentButtons={contentButtons}
        hasTextShadow={hasTextShadow}
      />
    </section>
  );
};

export default memo(ComplexTabList);
